// src/services/attendanceService.js
// =====================================================
// FuMind_GM HR Module - Phase 3E Attendance Service
// Break-aware checkout payload
// =====================================================

const HR_ATTENDANCE_GET_TODAY_FLOW_URL = "/tracka-flow/v1/HR_ATTENDANCE_GET_TODAY";
const HR_ATTENDANCE_CHECK_IN_FLOW_URL = "/tracka-flow/v1/HR_ATTENDANCE_CHECK_IN";
const HR_ATTENDANCE_CHECK_OUT_FLOW_URL = "/tracka-flow/v1/HR_ATTENDANCE_CHECK_OUT";
const HR_ATTENDANCE_GET_ALL_FLOW_URL = "/tracka-flow/v1/HR_ATTENDANCE_GET_ALL";
const HR_ATTENDANCE_GET_BY_EMPLOYEE_FLOW_URL = "/tracka-flow/v1/HR_ATTENDANCE_GET_BY_EMPLOYEE";

const HR_ATTENDANCE_BREAK_START_FLOW_URL = "/tracka-flow/v1/HR_ATTENDANCE_BREAK_START";
const HR_ATTENDANCE_BREAK_END_FLOW_URL = "/tracka-flow/v1/HR_ATTENDANCE_BREAK_END";
const HR_ATTENDANCE_REGULARIZATION_ACTION_FLOW_URL = "/tracka-flow/v1/HR_ATTENDANCE_REGULARIZATION_ACTION";


const API_KEY = "";

function assertUrlConfigured(url, flowName) {
  if (!url || url.startsWith("PASTE_")) {
    throw new Error(`${flowName} flow URL is not configured in attendanceService.js`);
  }
}

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text, rawText: text };
  }
}

function normalizePowerAutomateResponse(rawData) {
  return rawData?.body || rawData || {};
}

function normalizeAttendanceRecordsArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function throwIfFlowFailed(response, data, fallbackMessage) {
  if (!response.ok || data?.success === false) {
    const flowMessage = data?.message || data?.error?.message || data?.rawText || "";
    const statusText = `${response.status} ${response.statusText}`.trim();
    throw new Error(flowMessage ? `${fallbackMessage}: ${flowMessage}` : `${fallbackMessage}. HTTP ${statusText}`);
  }
}

function getLocalAttendanceDate(timeZone = "Asia/Kolkata") {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  return formatter.format(new Date());
}

export function getTodayAttendanceContext(timeZone = "Asia/Kolkata") {
  return {
    localAttendanceDate: getLocalAttendanceDate(timeZone),
    timeZone
  };
}

async function postToFlow(url, payload, fallbackMessage) {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, fallbackMessage);
  return data;
}

export async function getAttendanceToday({
  employeeId,
  workEmail = "",
  localAttendanceDate,
  timeZone = "Asia/Kolkata"
} = {}) {
  assertUrlConfigured(HR_ATTENDANCE_GET_TODAY_FLOW_URL, "HR_Attendance_Get_Today");
  if (!employeeId) throw new Error("Employee ID is required to load today's attendance.");

  const payload = {

    employeeId,
    workEmail,
    localAttendanceDate: localAttendanceDate || getLocalAttendanceDate(timeZone),
    timeZone
  };

  console.log("HR_Attendance_Get_Today payload:", payload);
  return postToFlow(HR_ATTENDANCE_GET_TODAY_FLOW_URL, payload, "Failed to load today's attendance");
}

export async function checkInAttendance({
  employeeId,
  workEmail = "",
  localAttendanceDate,
  timeZone = "Asia/Kolkata",
  checkInSource = "Web",
  locationText = ""
} = {}) {
  assertUrlConfigured(HR_ATTENDANCE_CHECK_IN_FLOW_URL, "HR_Attendance_Check_In");
  if (!employeeId) throw new Error("Employee ID is required for check-in.");

  const payload = {

    employeeId,
    workEmail,
    localAttendanceDate: localAttendanceDate || getLocalAttendanceDate(timeZone),
    timeZone,
    checkInSource,
    locationText
  };

  console.log("HR_Attendance_Check_In payload:", payload);
  return postToFlow(HR_ATTENDANCE_CHECK_IN_FLOW_URL, payload, "Failed to check in attendance");
}

export async function checkOutAttendance({
  employeeId,
  workEmail = "",
  localAttendanceDate,
  timeZone = "Asia/Kolkata",
  checkOutSource = "Web",
  remarks = "",
  breakMinutes = 0,
  breakDetails = ""
} = {}) {
  assertUrlConfigured(HR_ATTENDANCE_CHECK_OUT_FLOW_URL, "HR_Attendance_Check_Out");
  if (!employeeId) throw new Error("Employee ID is required for check-out.");

  const payload = {

    employeeId,
    workEmail,
    localAttendanceDate: localAttendanceDate || getLocalAttendanceDate(timeZone),
    timeZone,
    checkOutSource,
    remarks,
    breakMinutes,
    breakDetails
  };

  console.log("HR_Attendance_Check_Out payload:", payload);
  return postToFlow(HR_ATTENDANCE_CHECK_OUT_FLOW_URL, payload, "Failed to check out attendance");
}

export async function getAllAttendanceRecords({
  fromDate = "",
  toDate = "",
  country = "",
  employeeId = "",
  attendanceStatus = ""
} = {}) {
  assertUrlConfigured(HR_ATTENDANCE_GET_ALL_FLOW_URL, "HR_Attendance_Get_All");

  const data = await postToFlow(
    HR_ATTENDANCE_GET_ALL_FLOW_URL,
    {

      fromDate,
      toDate,
      country,
      employeeId,
      attendanceStatus
    },
    "Failed to load attendance records"
  );

  return normalizeAttendanceRecordsArray(data.attendanceRecords);
}

export async function getAttendanceByEmployee({
  employeeId,
  workEmail = "",
  fromDate = "",
  toDate = ""
} = {}) {
  assertUrlConfigured(HR_ATTENDANCE_GET_BY_EMPLOYEE_FLOW_URL, "HR_Attendance_Get_By_Employee");
  if (!employeeId) throw new Error("Employee ID is required to load attendance history.");

  const data = await postToFlow(
    HR_ATTENDANCE_GET_BY_EMPLOYEE_FLOW_URL,
    {

      employeeId,
      workEmail,
      fromDate,
      toDate
    },
    "Failed to load employee attendance history"
  );

  return normalizeAttendanceRecordsArray(data.attendanceRecords);
}
// src/services/attendanceService_with_break_flows.js
// Add these constants and functions into src/services/attendanceService.js



export async function startAttendanceBreak({
  employeeId,
  workEmail = "",
  localAttendanceDate,
  timeZone = "Asia/Kolkata",
  breakStartSource = "Web",
  remarks = ""
} = {}) {
  assertUrlConfigured(HR_ATTENDANCE_BREAK_START_FLOW_URL, "HR_Attendance_Break_Start");
  if (!employeeId) throw new Error("Employee ID is required to start break.");

  const payload = {

    employeeId,
    workEmail,
    localAttendanceDate: localAttendanceDate || getLocalAttendanceDate(timeZone),
    timeZone,
    breakStartSource,
    remarks
  };

  console.log("HR_Attendance_Break_Start payload:", payload);
  return postToFlow(HR_ATTENDANCE_BREAK_START_FLOW_URL, payload, "Failed to start break");
}

export async function endAttendanceBreak({
  employeeId,
  workEmail = "",
  localAttendanceDate,
  timeZone = "Asia/Kolkata",
  breakEndSource = "Web",
  remarks = ""
} = {}) {
  assertUrlConfigured(HR_ATTENDANCE_BREAK_END_FLOW_URL, "HR_Attendance_Break_End");
  if (!employeeId) throw new Error("Employee ID is required to end break.");

  const payload = {

    employeeId,
    workEmail,
    localAttendanceDate: localAttendanceDate || getLocalAttendanceDate(timeZone),
    timeZone,
    breakEndSource,
    remarks
  };

  console.log("HR_Attendance_Break_End payload:", payload);
  return postToFlow(HR_ATTENDANCE_BREAK_END_FLOW_URL, payload, "Failed to end break");
}


export async function actionAttendanceRegularization({
  id,
  attendanceId = "",
  employeeId = "",
  action,
  actionBy = "",
  actionByRole = "Manager",
  remarks = "",
  correctedCheckInTime = "",
  correctedCheckOutTime = "",
  correctedBreakMinutes = ""
} = {}) {
  assertUrlConfigured(HR_ATTENDANCE_REGULARIZATION_ACTION_FLOW_URL, "HR_Attendance_Regularization_Action");
  if (!id && !attendanceId) throw new Error("Attendance record ID is required for regularization action.");
  if (!action) throw new Error("Regularization action is required.");

  const payload = {

    id,
    attendanceId,
    employeeId,
    action,
    actionBy,
    actionByRole,
    remarks,
    correctedCheckInTime,
    correctedCheckOutTime,
    correctedBreakMinutes
  };
  console.log("HR_Attendance_Regularization_Action payload:", payload);
  return postToFlow(HR_ATTENDANCE_REGULARIZATION_ACTION_FLOW_URL, payload, "Failed to update attendance regularization");
}

export async function submitAttendanceRegularization({
  id,
  attendanceId = "",
  employeeId = "",
  actionBy = "",
  remarks = "",
  correctedCheckInTime = "",
  correctedCheckOutTime = "",
  correctedBreakMinutes = ""
} = {}) {
  return actionAttendanceRegularization({
    id,
    attendanceId,
    employeeId,
    action: "submitRegularization",
    actionBy,
    actionByRole: "Employee",
    remarks,
    correctedCheckInTime,
    correctedCheckOutTime,
    correctedBreakMinutes
  });
}
